import type { FBSContainerListResponse } from "@/shared/generated/ExpHubApi";
import { mapToAddressType, type Address } from "./Address";
import { mapToContractType, type ContractType } from "./ContractType";
import { mapToGasTariffType, type GasTariff } from "./GasTariff";
import {
  mapToUsageForecastItemType,
  type UsageForecastItem,
} from "./UsageForecastItem";

export interface GasContainer {
  serialNumber: string;
  name: string;
  address: Address;
  assetType?: string;
  serviceFlatRate?: number;
  equipmentNumber?: string;
  product: {
    name?: string;
    code?: string;
  };
  gasPrice: {
    includingVat?: number;
    excludingVat?: number;
    taxRate?: number;
    last: {
      excludingVat?: number;
    };
  };
  contract: {
    id: string;
    type: ContractType;
    startDate?: Date;
    endDate?: Date;
    version?: string;
  };
  volume: {
    max?: number;
    unit?: string;
  };
  meterNumber?: string;
  fillLevel: {
    currentPercentage?: number;
    forecastedEmptyTankDate?: Date;
  };
  daysUntilDelivery?: number;
  hasOpenOrder: boolean;
  tariffs: Array<GasTariff>;
  usageForecast: Array<UsageForecastItem>;
}

export const mapToGasContainerType = (
  data: FBSContainerListResponse["containers"][number],
  index: number,
): GasContainer => {
  return {
    serialNumber: data.serialNumber,
    name: `Mein Behälter ${index + 1}`, // TODO: Add name when its implemented in the backend -> data.name ? data.name : `Mein Behälter ${index+1}`,
    assetType: data.assetType,
    serviceFlatRate: data.serviceFlatRate,
    address: mapToAddressType(data.address),
    equipmentNumber: data.equipmentNumber,
    product: {
      name: data.product.name,
      code: data.product.code,
    },
    gasPrice: {
      includingVat: data.price.gross,
      excludingVat: data.price.net,
      taxRate: data.price.tax,
      last: {
        excludingVat: data.price.last?.net,
      },
    },
    contract: {
      id: data.contractId,
      type: mapToContractType(data.contract.type),
      startDate: data.contract.startDate
        ? new Date(data.contract.startDate)
        : undefined,
      endDate: data.contract.endDate
        ? new Date(data.contract.endDate)
        : undefined,
      version: data.contract.version,
    },
    volume: {
      max: data.volume.size,
      unit: data.volume.unit,
    },
    meterNumber: data.meterNumber,
    fillLevel: {
      currentPercentage: data.currentFillLevelPercentage,
      forecastedEmptyTankDate: data.forecastedEmptyTankDate
        ? new Date(data.forecastedEmptyTankDate)
        : undefined,
    },
    daysUntilDelivery: data.daysUntilDelivery,
    hasOpenOrder: data.openOrder ?? false,
    tariffs: data.bioTariffs?.map((tariff) => mapToGasTariffType(tariff)) ?? [],
    usageForecast:
      data.usageForecast?.map((forecast) =>
        mapToUsageForecastItemType(forecast),
      ) ?? [],
  };
};
